/**
 * Octopus - AlertCategorySection (Fase 1000)
 *
 * Bloco colapsável de uma categoria da página de alertas do equipamento
 * (Motor/Painel, Combustível, Bateria, Telemetria/RPM, Conectividade,
 * Geofence, Clima). Porta a affordance de expand/collapse já usada em
 * `frontend/src/components/monitor/SectionPanel.js` (ícone em contêiner
 * tonalizado + ChevronDown/ChevronRight). `children` é o conteúdo montado
 * pela página (linhas de regra + AlertRecipientsEditor).
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { severityConfig } from '@/lib/alertSeverity';

/**
 * @param {object} props
 * @param {React.ComponentType} [props.icon] - ícone lucide da categoria
 * @param {string} props.label - nome da categoria
 * @param {number} [props.ruleCount] - contador de regras exibido no header
 * @param {boolean} [props.hasOrphan] - true quando a categoria tem item(ns)
 *   WhatsApp/e-mail ligado(s) sem destinatário (AD-05)
 * @param {boolean} [props.defaultExpanded]
 * @param {React.ReactNode} props.children
 */
const AlertCategorySection = ({
  icon: Icon,
  label,
  ruleCount = 0,
  hasOrphan = false,
  defaultExpanded = true,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <Card className="bg-card border-border">
      <CardHeader className="py-3 px-4">
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold">
          {Icon && (
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary shrink-0">
              <Icon className="h-4 w-4" />
            </span>
          )}
          <span className="flex-1 truncate">{label}</span>
          <span className="text-xs font-normal text-muted-foreground tabular-nums">
            {ruleCount} {ruleCount === 1 ? 'regra' : 'regras'}
          </span>
          {hasOrphan && (
            <Badge
              variant="outline"
              className={cn(
                'text-xs',
                severityConfig.critical.bg,
                severityConfig.critical.color,
                severityConfig.critical.border,
              )}
            >
              sem destinatário
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-label={isExpanded ? 'Recolher seção' : 'Expandir seção'}
            title={isExpanded ? 'Recolher seção' : 'Expandir seção'}
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0 space-y-3">
          {ruleCount === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhuma regra de alerta configurada
            </p>
          )}
          {children}
        </CardContent>
      )}
    </Card>
  );
};

export default AlertCategorySection;
